import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Client } from '@/types/client';
import { useToast } from '@/hooks/use-toast';
import { eptaApi } from './services/eptaApi';
import { EptaIntegrationConfig, EptaIntegrationStatus } from './types/epta';

interface EptaIntegrationProps {
  client: Client;
}

const EptaIntegration: React.FC<EptaIntegrationProps> = ({ client }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [status, setStatus] = useState<EptaIntegrationStatus | null>(null);
  const [config, setConfig] = useState<EptaIntegrationConfig>({
    base_url: '',
    token: '',
    dias_cobranca_max: 3,
    dias_revistoria_min: 4,
    is_active: true,
  });
  const [testResult, setTestResult] = useState<string | null>(null);

  useEffect(() => {
    if (!client?.id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const s = await eptaApi.getIntegration(client.id);
        if (cancelled) return;
        setStatus(s);
        if (s) {
          setConfig({
            base_url: s.base_url,
            token: '',
            dias_cobranca_max: s.dias_cobranca_max,
            dias_revistoria_min: s.dias_revistoria_min,
            is_active: s.is_active,
          });
        }
      } catch (err: unknown) {
        toast({ title: 'Erro', description: 'Falha ao carregar config Epta', variant: 'destructive' });
      } finally {
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client?.id, toast]);

  const handleChange = <K extends keyof EptaIntegrationConfig>(field: K, value: EptaIntegrationConfig[K]) => {
    setConfig((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    // base_url é opcional; só validar formato se preenchida
    if (config.base_url && !/^https?:\/\//.test(config.base_url)) {
      toast({ title: 'URL inválida', description: 'Use http:// ou https:// (ou deixe em branco)', variant: 'destructive' });
      return;
    }
    if (!status?.has_token && !config.token) {
      toast({ title: 'Token obrigatório', description: 'Informe o Bearer Token', variant: 'destructive' });
      return;
    }
    if (config.dias_cobranca_max < 1) {
      toast({ title: 'Valor inválido', description: 'Cobrança máx deve ser >= 1', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      const payload: EptaIntegrationConfig = { ...config };
      if (!payload.token && status?.has_token) {
        toast({ title: 'Token necessário', description: 'Reinforme o token para salvar', variant: 'destructive' });
        setSaving(false);
        return;
      }
      await eptaApi.saveIntegration(client.id, payload);
      const refreshed = await eptaApi.getIntegration(client.id);
      setStatus(refreshed);
      toast({ title: 'Configuração salva' });
    } catch (err: unknown) {
      const msg = (err as { message?: string } | undefined)?.message ?? String(err);
      toast({ title: 'Erro ao salvar', description: msg, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await eptaApi.testConnection(client.id, {
        base_url: config.base_url || undefined,
        token: config.token || undefined,
      });
      if (r.ok) {
        setTestResult(`OK (${r.sample_count ?? 0} títulos retornados hoje)`);
        toast({ title: 'Conexão OK' });
      } else {
        setTestResult(`Falha: ${r.error ?? 'erro desconhecido'} (status ${r.status ?? '-'})`);
        toast({ title: 'Falha na conexão', description: r.error ?? 'erro desconhecido', variant: 'destructive' });
      }
    } catch (err: unknown) {
      const msg = (err as { message?: string } | undefined)?.message ?? String(err);
      setTestResult(`Erro: ${msg}`);
      toast({ title: 'Erro no teste', description: msg, variant: 'destructive' });
    } finally {
      setTesting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Integração Epta
          {status?.is_active ? (
            <Badge className="bg-green-100 text-green-800">
              <CheckCircle className="w-3 h-3 mr-1" />
              Ativa
            </Badge>
          ) : (
            <Badge variant="secondary">
              <AlertCircle className="w-3 h-3 mr-1" />
              Inativa
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" />
            Carregando...
          </div>
        )}

        <div>
          <Label htmlFor="epta-base-url">URL base (opcional)</Label>
          <Input
            id="epta-base-url"
            placeholder="https://api.epta.com.br/v1 (deixe em branco se ainda nao tiver)"
            value={config.base_url ?? ''}
            onChange={(e) => handleChange('base_url', e.target.value)}
          />
        </div>

        <div>
          <Label htmlFor="epta-token">Bearer Token {status?.has_token ? '(deixe em branco para manter o atual)' : ''}</Label>
          <Input
            id="epta-token"
            type="password"
            placeholder="Cole o token JWT"
            value={config.token}
            onChange={(e) => handleChange('token', e.target.value)}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="epta-cob-max">Cobrança até X dias após vencimento</Label>
            <Input
              id="epta-cob-max"
              type="number"
              min={1}
              value={config.dias_cobranca_max}
              onChange={(e) => handleChange('dias_cobranca_max', Number(e.target.value))}
            />
          </div>
          <div>
            <Label htmlFor="epta-rev-min">Revistoria a partir de X dias</Label>
            <Input
              id="epta-rev-min"
              type="number"
              min={1}
              value={config.dias_revistoria_min}
              onChange={(e) => handleChange('dias_revistoria_min', Number(e.target.value))}
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Switch
            id="epta-active"
            checked={config.is_active}
            onCheckedChange={(v) => handleChange('is_active', v)}
          />
          <Label htmlFor="epta-active">Integração ativa</Label>
        </div>

        <div className="flex gap-2">
          <Button onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Salvar'}
          </Button>
          <Button variant="outline" onClick={handleTest} disabled={testing}>
            {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Testar Conexão'}
          </Button>
        </div>

        {testResult && (
          <div className="text-sm text-muted-foreground">{testResult}</div>
        )}
      </CardContent>
    </Card>
  );
};

export default EptaIntegration;
